import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, MapPin, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const formatRange = (startsAt, endsAt) => {
  if (!startsAt) return '';
  try {
    const s = new Date(startsAt);
    const opts = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOpts = { hour: 'numeric', minute: '2-digit' };
    const dateStr = s.toLocaleDateString(undefined, opts);
    const timeStr = s.toLocaleTimeString(undefined, timeOpts);
    if (endsAt) {
      const e = new Date(endsAt);
      const sameDay = s.toDateString() === e.toDateString();
      const endTime = e.toLocaleTimeString(undefined, timeOpts);
      return sameDay
        ? `${dateStr} · ${timeStr} – ${endTime}`
        : `${dateStr} ${timeStr} → ${e.toLocaleDateString(undefined, opts)} ${endTime}`;
    }
    return `${dateStr} · ${timeStr}`;
  } catch {
    return startsAt;
  }
};

const monthBadge = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return {
      month: d.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(),
      day: d.getDate(),
    };
  } catch {
    return null;
  }
};

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/events?limit=6&upcoming_only=true`)
      .then((res) => setEvents(res.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (events.length === 0) return null; // Hide section entirely if there's nothing upcoming.

  return (
    <section
      id="upcoming-events"
      className="py-16 sm:py-20 bg-white"
      data-testid="upcoming-events-section"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-church-green/10 rounded-full mb-4">
            <Calendar className="w-4 h-4 text-church-green" />
            <span className="text-sm font-semibold text-church-green">What's Coming Up</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">Upcoming Events</h2>
          <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
            Mark your calendar and join us for what's next.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {events.map((event) => {
            const badge = monthBadge(event.starts_at);
            const hasImage = event.image_url && event.image_url.length > 0;

            return (
              <article
                key={event.id}
                className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-church-green hover:shadow-xl transition-all duration-300"
                data-testid="event-card"
              >
                {hasImage && (
                  <div
                    className="h-40 w-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${event.image_url})` }}
                  />
                )}
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-3">
                    {badge && (
                      <div className="flex-shrink-0 text-center bg-church-green text-white rounded-lg px-3 py-2 leading-tight">
                        <div className="text-[10px] font-bold tracking-wider opacity-90">
                          {badge.month}
                        </div>
                        <div className="text-2xl font-bold">{badge.day}</div>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-church-green transition-colors line-clamp-2">
                        {event.title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatRange(event.starts_at, event.ends_at)}
                      </p>
                    </div>
                  </div>

                  {event.description && (
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-3">
                      {event.description}
                    </p>
                  )}

                  {event.location && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-4">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{event.location}</span>
                    </div>
                  )}

                  {event.registration_url && (
                    <a
                      href={event.registration_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-church-green hover:gap-2.5 transition-all"
                      data-testid="event-rsvp-link"
                    >
                      RSVP / Register
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {events.length >= 6 && (
          <div className="text-center mt-10">
            <Button
              asChild
              variant="outline"
              className="border-church-green text-church-green hover:bg-church-green hover:text-white"
            >
              <a href="/events">
                See all events
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </a>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingEvents;
